import {cn} from '@tempmee/core'
import {type InputHTMLAttributes, type ReactNode, forwardRef, useId, useState} from 'react'
import {FieldBox, FieldHelperText} from './FieldLabel'
import {inputBaseClass, inputBoxClass, inputIconWrapperClass} from './InputTextFieldVariants'

export interface InputTextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Applies error styling (red border + background) */
  isError?: boolean
  /** Icon rendered on the left side of the input */
  leftIcon?: ReactNode
  /** Icon rendered on the right side of the input */
  rightIcon?: ReactNode
  /** Disables the input and dims text to the disabled token color */
  disabled?: boolean
  /**
   * Floating label rendered inside the box, which doubles as the placeholder
   * when the field is empty and unfocused.
   *
   * Optional, and omitting it leaves the field exactly as it was — an unlabelled
   * box whose markup is unchanged. Supplied, the field grows the same embedded
   * label `TextField` has, so a labelled field can be used without wrapping it
   * in `FormField`.
   */
  label?: ReactNode
  /** Helper or validation message rendered below the field. Needs no `label`. */
  helperText?: ReactNode
}

export const InputTextField = forwardRef<HTMLInputElement, InputTextFieldProps>(
  (
    {
      className,
      isError,
      leftIcon,
      rightIcon,
      disabled,
      label,
      helperText,
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      id,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId()
    const inputId = id ?? generatedId
    const helperId = `${inputId}-helper`

    const isControlled = value !== undefined
    const [focused, setFocused] = useState(false)
    const [uncontrolledFilled, setUncontrolledFilled] = useState(
      defaultValue != null && String(defaultValue).length > 0,
    )
    const filled = isControlled ? value != null && String(value).length > 0 : uncontrolledFilled
    // The label floats up once the field is focused or holds a value.
    const shrink = focused || filled

    const box = (
      <div className={cn(inputBoxClass({isError, disabled}), className)}>
        {leftIcon && <span className={inputIconWrapperClass}>{leftIcon}</span>}
        {/*
          Labelled, the input needs a positioned parent for the label to pin to,
          and gives up `flex-1` to it. Unlabelled, it renders bare — the same
          markup as before these props existed.
        */}
        <FieldBox
          label={label}
          htmlFor={inputId}
          shrink={shrink}
          disabled={disabled}
          isError={isError}
        >
          <input
            ref={ref}
            id={inputId}
            className={cn(inputBaseClass, label != null && 'w-full pt-3')}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            // A labelled field hides its placeholder until the label has floated
            // out of the way, or the two sit on top of each other.
            placeholder={label == null || shrink ? placeholder : undefined}
            aria-describedby={helperText ? helperId : undefined}
            onFocus={(e) => {
              setFocused(true)
              onFocus?.(e)
            }}
            onBlur={(e) => {
              setFocused(false)
              onBlur?.(e)
            }}
            onChange={(e) => {
              if (!isControlled) setUncontrolledFilled(e.target.value.length > 0)
              onChange?.(e)
            }}
            {...props}
          />
        </FieldBox>
        {rightIcon && <span className={inputIconWrapperClass}>{rightIcon}</span>}
      </div>
    )

    // Unlabelled and helperless, the box IS the field — same markup as before
    // these props existed.
    if (label == null && helperText == null) return box

    return (
      <div className="flex w-full flex-col gap-2">
        {box}
        {helperText != null && (
          <FieldHelperText id={helperId} disabled={disabled} isError={isError}>
            {helperText}
          </FieldHelperText>
        )}
      </div>
    )
  },
)

InputTextField.displayName = 'InputTextField'
